import React, { useState } from "react";
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Image, Platform, SafeAreaView, ActivityIndicator } from "react-native";
import { useAuth } from "../context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";

const Login = () => {
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [rememberMe, setRememberMe] = useState(false);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const { login, signUp } = useAuth();
	const navigate = useNavigate();
	const location = useLocation();

	// Get the intended destination from location state, or default to home
	const from = location.state?.from || "/";

	const handleLogin = async () => {
		if (!email || !password) {
			setError("Please enter both email and password");
			return;
		}

		setError("");
		setLoading(true);

		try {
			const success = await login(email, password);
			if (!success) {
				setError("Invalid credentials. Please try again.");
			} else {
				// Navigate to the intended destination on successful login
				navigate(from, { replace: true });
			}
		} catch (err) {
			setError(err.message || "An error occurred during login");
		} finally {
			setLoading(false);
		}
	};

	const handleGoogleSignIn = () => {
		// Implement OAuth sign-in
		window.location.href = `https://${process.env.REACT_APP_COGNITO_DOMAIN}/oauth2/authorize?client_id=${process.env.REACT_APP_COGNITO_CLIENT_ID}&response_type=code&scope=email+openid+profile&redirect_uri=${window.location.origin}`;
	};

	return (
		<SafeAreaView style={styles.container}>
			<View style={styles.content}>
				{/* Logo */}
				<View style={styles.logoContainer}>
					<View style={styles.logoBox}>
						<Image source={require("../assets/icon.png")} style={styles.logo} resizeMode="contain" />
					</View>
				</View>

				{/* Welcome Text */}
				<View style={styles.welcomeContainer}>
					<Text style={styles.welcomeTitle}>Welcome back</Text>
					<Text style={styles.welcomeSubtitle}>Sign in to start making your idea a reality</Text>
					{error ? <Text style={styles.errorText}>{error}</Text> : null}
				</View>

				{/* Form */}
				<View style={styles.form}>
					<View style={styles.inputContainer}>
						<Text style={styles.label}>Email</Text>
						<TextInput style={styles.input} placeholder="Enter your email" placeholderTextColor="#666" value={email} onChangeText={setEmail} autoCapitalize="none" keyboardType="email-address" editable={!loading} />
					</View>

					<View style={styles.inputContainer}>
						<Text style={styles.label}>Password</Text>
						<TextInput style={styles.input} placeholder="Enter your password" placeholderTextColor="#666" value={password} onChangeText={setPassword} secureTextEntry editable={!loading} />
					</View>

					<View style={styles.rememberContainer}>
						<TouchableOpacity style={styles.checkboxContainer} onPress={() => !loading && setRememberMe(!rememberMe)}>
							<View style={[styles.checkbox, rememberMe && styles.checkboxChecked]} />
							<Text style={styles.checkboxLabel}>Remember me</Text>
						</TouchableOpacity>
						<TouchableOpacity onPress={() => navigate("/forgot-password")} disabled={loading}>
							<Text style={styles.forgotPassword}>Forgot password</Text>
						</TouchableOpacity>
					</View>

					<TouchableOpacity style={[styles.signInButton, loading && styles.buttonDisabled]} onPress={handleLogin} disabled={loading}>
						{loading ? <ActivityIndicator color="white" /> : <Text style={styles.signInButtonText}>Sign in</Text>}
					</TouchableOpacity>

					{false && (
						<TouchableOpacity style={[styles.googleButton, loading && styles.buttonDisabled]} onPress={handleGoogleSignIn} disabled={loading}>
							<Image
								source={{
									uri: "https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg",
								}}
								style={styles.googleIcon}
							/>
							<Text style={styles.googleButtonText}>Sign in with Google</Text>
						</TouchableOpacity>
					)}
				</View>

				<View style={styles.signupContainer}>
					<Text style={styles.signupText}>Don't have an account?</Text>
					<TouchableOpacity onPress={() => navigate("/signup")} disabled={loading}>
						<Text style={styles.signupLink}>Sign up</Text>
					</TouchableOpacity>
				</View>
			</View>
		</SafeAreaView>
	);
};

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: "#000000",
	},
	content: {
		flex: 1,
		padding: 24,
		justifyContent: "center",
		maxWidth: 400,
		alignSelf: "center",
		width: "100%",
	},
	logoContainer: {
		alignItems: "center",
		marginBottom: 32,
	},
	logoBox: {
		width: 200,
		height: 48,
		backgroundColor: "transparent",
		borderRadius: 12,
		justifyContent: "center",
		alignItems: "center",
	},
	logo: {
		width: 150,
		height: 150,
		shadowColor: "#fff",
		shadowOffset: { width: 0, height: 0 },
		shadowOpacity: 0.9,
		shadowRadius: 10,
		elevation: 10,
		...(Platform.OS === "web" ? { boxShadow: "0 0 10px 10px #ffffff" } : {}),
	},
	welcomeContainer: {
		alignItems: "center",
		marginBottom: 32,
	},
	welcomeTitle: {
		fontSize: 24,
		fontWeight: "bold",
		color: "white",
		marginBottom: 8,
	},
	welcomeSubtitle: {
		fontSize: 16,
		color: "#666",
	},
	form: {
		gap: 16,
	},
	inputContainer: {
		marginBottom: 16,
	},
	label: {
		color: "#666",
		marginBottom: 8,
		fontSize: 14,
	},
	input: {
		backgroundColor: "#1A1A1A",
		borderWidth: 1,
		borderColor: "#333",
		borderRadius: 6,
		padding: 12,
		color: "white",
		fontSize: 14,
	},
	rememberContainer: {
		flexDirection: "row",
		justifyContent: "space-between",
		alignItems: "center",
		marginBottom: 16,
	},
	checkboxContainer: {
		flexDirection: "row",
		alignItems: "center",
	},
	checkbox: {
		width: 20,
		height: 20,
		borderWidth: 1,
		borderColor: "#333",
		borderRadius: 4,
		marginRight: 8,
	},
	checkboxChecked: {
		backgroundColor: "#0066FF",
		borderColor: "#0066FF",
	},
	checkboxLabel: {
		color: "#666",
		fontSize: 14,
	},
	forgotPassword: {
		color: "#0066FF",
		fontSize: 14,
	},
	signInButton: {
		backgroundColor: "#0066FF",
		padding: 16,
		borderRadius: 6,
		alignItems: "center",
		marginBottom: 16,
	},
	signInButtonText: {
		color: "white",
		fontSize: 14,
		fontWeight: "500",
	},
	googleButton: {
		backgroundColor: "white",
		padding: 16,
		borderRadius: 6,
		flexDirection: "row",
		alignItems: "center",
		justifyContent: "center",
	},
	googleIcon: {
		width: 20,
		height: 20,
		marginRight: 8,
	},
	googleButtonText: {
		color: "black",
		fontSize: 14,
		fontWeight: "500",
	},
	signupContainer: {
		flexDirection: "row",
		justifyContent: "center",
		alignItems: "center",
		marginTop: 32,
		gap: 4,
	},
	signupText: {
		color: "#666",
		fontSize: 14,
	},
	signupLink: {
		color: "#0066FF",
		fontSize: 14,
	},
	errorText: {
		color: "#ff4444",
		marginTop: 8,
		fontSize: 14,
		textAlign: "center",
	},
	buttonDisabled: {
		opacity: 0.7,
	},
});

export default Login;
